import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ExternalLink, Minus, Plus, ShoppingBag } from "lucide-react";

import { api, openExternal } from "@/lib/api";
import { titleCase } from "@/lib/options";
import { ProductPlaceholder } from "@/components/cards";
import { firstAvailable, PriceTag, StockBadge, VariantPicker } from "@/components/shopui";
import { Button, Card, IconCircle, ScreenHeader, Spinner, Tag } from "@/components/kit";
import { Page } from "@/components/layout";
import { Footer } from "@/pages/ScanResults";
import { useShop } from "@/shop";

const SEE = "See breez.au";

export default function Product() {
  const { slug } = useParams();
  const { catalog, addToCart, cartBusy } = useShop();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [variantId, setVariantId] = useState(undefined);
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);
  const navigate = useNavigate();
  const isKit = !!slug?.startsWith("kit-");

  const load = useCallback(async () => {
    try {
      setError(null);
      if (isKit) {
        if (!catalog) return;
        const k = catalog.kits.find((x) => `kit-${x.handle}` === slug);
        if (!k) throw new Error(catalog.live ? "Kit not found." : "Live prices unavailable — view on breez.au.");
        setP({ slug, name: k.title, tagline: "BREEZ kit", description: "", benefits: [], suitable_hair_types: [], hold: "", finish: "", ingredients: "", directions: "", shopify_url: k.url, shopify: k });
      } else setP(await api(`/products/${slug}`));
    } catch (e) {
      setError(e.message);
    }
  }, [slug, isKit, catalog]);
  useEffect(() => {
    load();
  }, [load]);

  const live = p?.shopify ?? null;
  const variant = live ? live.variants.find((v) => v.id === variantId) ?? firstAvailable(live) : undefined;
  const rows = p
    ? [
        ["Hold", p.hold === "none" ? "No hold (prep)" : p.hold ? titleCase(p.hold) : ""],
        ["Finish", p.finish ? titleCase(p.finish) : ""],
        ["Suits", (p.suitable_hair_types ?? []).map(titleCase).join(", ")],
        ["Ingredients", p.ingredients || SEE],
        ["Directions", p.directions || SEE],
      ].filter(([, v]) => v)
    : [];

  return (
    <div data-testid="product-detail-screen">
      <ScreenHeader testId="product-back-button" label="Product" fallback="/shop" />
      {error ? (
        <div className="flex flex-col items-center gap-3 p-8">
          <p data-testid="product-error" className="t-body">{error}</p>
          <Button testId="product-retry" title="Retry" variant="outline" onClick={load} />
        </div>
      ) : !p ? <div className="flex justify-center p-12"><Spinner /></div> : (
        <>
          <Page wide className="grid gap-6 pb-32 md:grid-cols-2 md:gap-10">
            <div className="flex flex-col gap-2">
              {live?.images?.length ? (
                <>
                  <img data-testid="product-gallery" src={live.images[img]} alt={live.title} className="aspect-square w-full rounded-[20px] bg-tert object-cover" />
                  {live.images.length > 1 ? (
                    <div className="no-scrollbar flex gap-2 overflow-x-auto">
                      {live.images.map((u, i) => (
                        <button key={u} type="button" data-testid={`product-thumb-${i}`} aria-label={`Photo ${i + 1}`} onClick={() => setImg(i)} className={`focus-ring h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 ${i === img ? "border-ocean" : "border-transparent"}`}>
                          <img src={u} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : <ProductPlaceholder name={p.name} height={300} />}
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <p className="t-label">{p.tagline}</p>
                <h1 data-testid="product-name" className="t-h1">{(live?.title ?? p.name).toUpperCase()}</h1>
              </div>
              <Card testId="product-buy-box" className="flex flex-col gap-3">
                {live ? (
                  <>
                    <div className="flex items-center justify-between"><PriceTag variant={variant} size="lg" testId="product-price" /><StockBadge variant={variant} testId="product-stock" /></div>
                    <VariantPicker live={live} value={variant?.id} onChange={setVariantId} testPrefix="product-variant" />
                    <div className="flex items-center justify-between">
                      <span className="t-label">Quantity</span>
                      <div className="flex items-center gap-3">
                        <IconCircle testId="product-qty-minus" label="Decrease quantity" icon={Minus} onClick={() => setQty((q) => Math.max(1, q - 1))} />
                        <span data-testid="product-qty" className="t-title min-w-[20px] text-center">{qty}</span>
                        <IconCircle testId="product-qty-plus" label="Increase quantity" icon={Plus} onClick={() => setQty((q) => Math.min(10, q + 1))} />
                      </div>
                    </div>
                  </>
                ) : <p data-testid="product-live-unavailable" className="t-strong !text-mute">Live prices unavailable — view on breez.au.</p>}
              </Card>
              <p data-testid="product-description" className="t-body">{live?.description || p.description}</p>
              {p.benefits?.length ? <div className="flex flex-wrap gap-2">{p.benefits.map((b) => <Tag key={b} label={b} />)}</div> : null}
              <Card testId="product-specs" className="!py-1">
                {rows.map(([k, v], i) => (
                  <div key={k} className={`flex items-start gap-3 py-3 ${i < rows.length - 1 ? "border-b border-divider" : ""}`}>
                    <span className="t-label w-24 shrink-0">{k}</span>
                    <span data-testid={`product-spec-${k.toLowerCase()}`} className={`t-strong flex-1 ${v === SEE ? "!text-mute" : ""}`}>{v}</span>
                  </div>
                ))}
              </Card>
              <p className="t-caption">Always follow the directions on the pack.</p>
              {p.used_in?.length ? (
                <section className="flex flex-col gap-3">
                  <h2 className="t-h2">Used in {p.used_in.length} styles</h2>
                  <div className="no-scrollbar flex gap-3 overflow-x-auto">
                    {p.used_in.map((s) => (
                      <button key={s.slug} type="button" data-testid={`product-used-in-${s.slug}`} onClick={() => navigate(`/style/${s.slug}`)} className="focus-ring w-[120px] shrink-0 rounded-xl text-left">
                        <img src={s.image_url} alt="" className="h-[150px] w-[120px] rounded-xl bg-tert object-cover" />
                        <span className="t-strong mt-1.5 block truncate">{s.name}</span>
                      </button>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          </Page>
          <Footer>
            <Button testId="product-buy-button" title="breez.au" icon={ExternalLink} variant="outline" onClick={() => openExternal(live?.url ?? p.shopify_url)} className="flex-1" />
            {live ? (
              <Button testId="product-add-to-cart" title={variant?.available ? "Add to cart" : "Sold out"} icon={variant?.available ? ShoppingBag : undefined} disabled={!variant?.available || cartBusy} onClick={() => variant && addToCart([{ variant_id: variant.id, quantity: qty }])} className="flex-[2] !px-3" />
            ) : null}
          </Footer>
        </>
      )}
    </div>
  );
}
